/**
 * CampaignCreator (dawniej PosterGenerator)
 * "Kampania marketingowa" — język klienta, flow: CO TO → WYBIERZ → STYL → GOTOWE → CO TERAZ
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import html2canvas from 'html2canvas';
import { Button } from '@/components/ui/button';
import PanelLayout from '@/components/PanelLayout';
import AssistantPanel from '@/components/AssistantPanel';
import { API } from '@/utils/api';
import { getImageUrl } from '@/utils/imageUrl';
import {
  Dog, Baby, Building2, Eye, Download, Loader2,
  ChevronRight, Sparkles, Rocket, ArrowRight,
  Facebook, Calendar, Inbox, Check
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

/* ─── POSTER TEMPLATE (unchanged rendering) ─── */
function PosterTemplate({ data, theme }) {
  if (!data) return null;
  const isGold = theme === 'gold';
  const bg = isGold ? '#0B0F1A' : '#F8F6F2';
  const primary = isGold ? '#D6B36A' : '#1A1A1A';
  const textMain = isGold ? '#FFFFFF' : '#1A1A1A';
  const textMuted = isGold ? '#94A3B8' : 'var(--zinc-500, #71717a)';
  const borderCol = isGold ? 'rgba(214,179,106,0.15)' : 'rgba(0,0,0,0.06)';

  return (
    <div style={{ width: 1080, height: 1350, background: bg, fontFamily: "'Cinzel', 'Inter', serif", position: 'relative', display: 'flex', flexDirection: 'column' }}>
      {data.image_url && (
        <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
          <img src={getImageUrl(data.image_url)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} crossOrigin="anonymous" />
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '60%', background: `linear-gradient(transparent, ${bg})` }} />
        </div>
      )}
      <div style={{ padding: '40px 60px 50px', position: 'relative' }}>
        <div style={{ fontSize: 16, color: primary, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 3, marginBottom: 16, fontFamily: "'Inter', sans-serif" }}>
          {data.breed || 'APISAI'}
        </div>
        <h1 style={{ fontSize: 52, fontWeight: 700, color: textMain, margin: '0 0 16px', lineHeight: 1.1, fontFamily: "'Cinzel', serif" }}>
          {data.title}
        </h1>
        {data.subtitle && <p style={{ fontSize: 18, color: textMuted, margin: '0 0 30px', fontFamily: "'Inter', sans-serif" }}>{data.subtitle}</p>}
        {data.traits && data.traits.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 30 }}>
            {data.traits.slice(0, 6).map((t, i) => (
              <span key={i} style={{ padding: '8px 20px', borderRadius: 999, border: `1px solid ${borderCol}`, color: textMuted, fontSize: 14, fontFamily: "'Inter', sans-serif" }}>{t}</span>
            ))}
          </div>
        )}
        {data.contact && (
          <div style={{ borderTop: `1px solid ${borderCol}`, paddingTop: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              {data.contact.kennel_name && <p style={{ fontSize: 16, fontWeight: 600, color: textMain, margin: 0, fontFamily: "'Inter', sans-serif" }}>{data.contact.kennel_name}</p>}
              {data.contact.city && <p style={{ fontSize: 14, color: textMuted, margin: '4px 0 0', fontFamily: "'Inter', sans-serif" }}>{data.contact.city}</p>}
            </div>
            <div style={{ textAlign: 'right' }}>
              {data.contact.phone && <p style={{ fontSize: 14, color: primary, margin: 0, fontWeight: 500 }}>{data.contact.phone}</p>}
              {data.contact.email && <p style={{ fontSize: 13, color: textMuted, margin: '2px 0 0' }}>{data.contact.email}</p>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── USER-FACING LABELS (język klienta) ─── */
const OFFER_TABS = [
  { id: 'stud_dog', label: 'Twój pies', icon: Dog, emptyTitle: 'Nie masz jeszcze dodanego psa', emptyDesc: 'Dodaj psa, aby stworzyć reklamę i zacząć zdobywać klientów.', emptyCta: 'Dodaj psa', emptyTo: '/wizard/reproduktor' },
  { id: 'litter', label: 'Szczenięta', icon: Baby, emptyTitle: 'Nie masz jeszcze dodanych szczeniąt', emptyDesc: 'Dodaj miot, aby stworzyć reklamę i przyciągnąć zainteresowanych.', emptyCta: 'Dodaj miot', emptyTo: '/wizard/miot' },
  { id: 'kennel', label: 'Twoja hodowla', icon: Building2, emptyTitle: 'Nie masz jeszcze hodowli', emptyDesc: 'Utwórz profil hodowli, aby móc tworzyć materiały reklamowe.', emptyCta: 'Uzupełnij w profilu', emptyTo: '/panel/profil' },
];

export default function PosterGenerator() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState(0); // 0=intro, 1=select, 2=style, 3=preview, 4=success
  const [entityType, setEntityType] = useState('stud_dog');
  const [allEntities, setAllEntities] = useState({ stud_dog: [], litter: [], kennel: [] });
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [theme, setTheme] = useState('gold');
  const [posterData, setPosterData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const posterRef = useRef(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/poster/entities`, { headers })
      .then(r => setAllEntities({
        stud_dog: r.data.stud_dog || [],
        litter: r.data.litter || [],
        kennel: r.data.kennel || [],
      }))
      .catch(console.error);
  }, []); // eslint-disable-line

  const entities = allEntities[entityType] || [];
  const currentTab = OFFER_TABS.find(t => t.id === entityType);

  const generate = useCallback(async () => {
    if (!selectedEntity) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/poster/generate`, {
        entity_type: entityType,
        entity_id: selectedEntity.id,
        theme,
      }, { headers });
      setPosterData(res.data);
      setStep(3);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [selectedEntity, theme, entityType]); // eslint-disable-line

  const exportPNG = useCallback(async () => {
    if (!posterRef.current) return;
    setExporting(true);
    try {
      const canvas = await html2canvas(posterRef.current, { scale: 2, useCORS: true, allowTaint: true, backgroundColor: null, width: 1080, height: 1350 });
      const link = document.createElement('a');
      link.download = `${posterData?.title || 'kampania'}-apisai.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (e) { console.error('Export failed:', e); }
    finally { setExporting(false); }
  }, [posterData]);

  return (
    <PanelLayout>
      <link href="https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;700&family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet" />

      <div className="max-w-[1200px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          <div className="lg:col-span-8">

            {/* HEADER */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-[22px] font-bold text-zinc-900" data-testid="campaign-title">Kampania marketingowa</h1>
                <p className="text-[14px] text-zinc-500 mt-0.5">Masz gotowe narzędzie — zacznij zdobywać klientów.</p>
              </div>
              {step > 0 && step < 4 && (
                <div className="flex items-center gap-2 text-sm" data-testid="steps-indicator">
                  {['Wybierz ofertę', 'Styl reklamy', 'Gotowe'].map((label, i) => (
                    <span key={i} className="flex items-center gap-2">
                      {i > 0 && <ChevronRight className="w-3.5 h-3.5 text-zinc-300" />}
                      <span className={`px-3 py-1 rounded-full text-xs font-medium transition ${
                        step === i + 1 ? 'bg-[#F47A3B]/10 text-[#F47A3B] border border-[#F47A3B]/20'
                        : step > i + 1 ? 'bg-emerald-50 text-emerald-600'
                        : 'text-[#94A3B8]'
                      }`}>{label}</span>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* ─── STEP 0: INTRO — "Jak to dziala?" ─── */}
            {step === 0 && (
              <div data-testid="step-intro">
                <div className="bg-white rounded-2xl border border-zinc-200 p-8 shadow-[0_1px_3px_rgba(0,0,0,0.04)] mb-5">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-xl bg-[#F47A3B] flex items-center justify-center">
                      <Sparkles className="w-5 h-5 text-white" />
                    </div>
                    <h2 className="text-[18px] font-bold text-zinc-900">Jak to dziala?</h2>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                    <div className="text-center">
                      <div className="w-12 h-12 rounded-2xl bg-[#F47A3B]/10 flex items-center justify-center mx-auto mb-3">
                        <span className="text-[18px] font-bold text-[#F47A3B]">1</span>
                      </div>
                      <h3 className="text-[14px] font-bold text-zinc-900 mb-1">Wybierasz co chcesz sprzedac</h3>
                      <p className="text-[13px] text-zinc-500">Szczenieta, psa, hodowle lub usluge.</p>
                    </div>
                    <div className="text-center">
                      <div className="w-12 h-12 rounded-2xl bg-[#F47A3B]/10 flex items-center justify-center mx-auto mb-3">
                        <span className="text-[18px] font-bold text-[#F47A3B]">2</span>
                      </div>
                      <h3 className="text-[14px] font-bold text-zinc-900 mb-1">System tworzy reklamę</h3>
                      <p className="text-[13px] text-zinc-500">Na podstawie Twoich danych powstaje profesjonalna grafika.</p>
                    </div>
                    <div className="text-center">
                      <div className="w-12 h-12 rounded-2xl bg-[#F47A3B]/10 flex items-center justify-center mx-auto mb-3">
                        <span className="text-[18px] font-bold text-[#F47A3B]">3</span>
                      </div>
                      <h3 className="text-[14px] font-bold text-zinc-900 mb-1">Klienci trafiaja do Ciebie</h3>
                      <p className="text-[13px] text-zinc-500">Publikujesz na Facebooku, Instagramie lub gdziekolwiek chcesz.</p>
                    </div>
                  </div>

                  <div className="flex justify-center mt-8">
                    <Button onClick={() => setStep(1)} className="bg-[#F47A3B] hover:bg-[#E06A2B] text-white rounded-xl px-8 py-3 text-[15px] font-semibold" data-testid="start-campaign-btn">
                      <Rocket className="w-5 h-5 mr-2" />
                      Stwórz reklamę
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* ─── STEP 1: Select offer (język klienta) ─── */}
            {step === 1 && (
              <div data-testid="step-select-entity">
                <h2 className="text-[18px] font-bold text-zinc-900 mb-2">Co chcesz promowac?</h2>
                <p className="text-[14px] text-zinc-500 mb-6">Dane zostanom automatycznie pobrane z Twojego profilu.</p>

                <div className="flex gap-6 mb-6 border-b border-zinc-200" data-testid="entity-type-tabs">
                  {OFFER_TABS.map(tab => {
                    const Icon = tab.icon;
                    const count = allEntities[tab.id]?.length || 0;
                    return (
                      <button key={tab.id} onClick={() => { setEntityType(tab.id); setSelectedEntity(null); }}
                        data-testid={`tab-${tab.id}`}
                        className={`flex items-center gap-2 px-1 pb-3 text-sm font-medium transition-colors relative ${
                          entityType === tab.id
                            ? 'text-[#F47A3B]'
                            : 'text-zinc-400 hover:text-zinc-700'
                        }`}>
                        <Icon className="w-4 h-4" />
                        {tab.label} ({count})
                        {entityType === tab.id && (
                          <span className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#F47A3B] rounded-full" />
                        )}
                      </button>
                    );
                  })}
                </div>

                {entities.length === 0 ? (
                  <div className="text-center py-12 bg-white border border-zinc-200 rounded-2xl" data-testid="empty-entities">
                    {currentTab && <currentTab.icon className="w-12 h-12 text-zinc-300 mx-auto mb-4" />}
                    <h3 className="text-[16px] font-bold text-zinc-900 mb-2">{currentTab?.emptyTitle}</h3>
                    <p className="text-[14px] text-zinc-500 mb-5 max-w-sm mx-auto">{currentTab?.emptyDesc}</p>
                    <Button onClick={() => navigate(currentTab?.emptyTo || '/panel')} className="bg-[#F47A3B] hover:bg-[#E06A2B] text-white rounded-xl px-6">
                      {currentTab?.emptyCta}
                    </Button>
                  </div>
                ) : (
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {entities.map(e => (
                      <button key={e.id} onClick={() => { setSelectedEntity(e); setStep(2); }}
                        data-testid={`entity-${e.id}`}
                        className={`flex items-center gap-4 p-4 rounded-xl border text-left transition hover:border-[#F47A3B]/40 ${
                          selectedEntity?.id === e.id ? 'bg-[#F47A3B]/10 border-[#F47A3B]/30' : 'bg-white border-zinc-200'
                        }`}>
                        <div className="w-14 h-14 rounded-xl bg-[#F3F4F6] overflow-hidden flex-shrink-0">
                          {getImageUrl(e.photo) ? (
                            <img src={getImageUrl(e.photo)} alt={e.label} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              {currentTab && <currentTab.icon className="w-6 h-6 text-zinc-300" />}
                            </div>
                          )}
                        </div>
                        <span className="text-zinc-900 font-medium text-sm line-clamp-2">{e.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ─── STEP 2: Select style ─── */}
            {step === 2 && (
              <div data-testid="step-select-theme">
                <h2 className="text-[18px] font-bold text-zinc-900 mb-2">Wybierz styl reklamy</h2>
                <p className="text-[14px] text-zinc-500 mb-6">
                  Reklama dla: <span className="text-[#F47A3B] font-medium">{selectedEntity?.label}</span>
                </p>

                <div className="grid gap-4 sm:grid-cols-2 max-w-xl">
                  <button onClick={() => setTheme('gold')} data-testid="theme-gold"
                    className={`rounded-2xl border p-1 transition ${theme === 'gold' ? 'border-[#F47A3B] ring-2 ring-[#F47A3B]/30' : 'border-zinc-200 hover:border-zinc-300'}`}>
                    <div className="rounded-xl overflow-hidden" style={{ background: '#0B0F1A' }}>
                      <div className="p-6 text-center">
                        <div className="w-16 h-16 mx-auto rounded-full mb-3" style={{ background: '#D6B36A' }} />
                        <p style={{ color: '#D6B36A', fontFamily: "'Cinzel', serif" }} className="text-lg font-bold">Premium</p>
                        <p className="text-zinc-400 text-xs mt-1">Elegancki, ciemne tlo</p>
                      </div>
                    </div>
                  </button>
                  <button onClick={() => setTheme('light')} data-testid="theme-light"
                    className={`rounded-2xl border p-1 transition ${theme === 'light' ? 'border-[#F47A3B] ring-2 ring-[#F47A3B]/30' : 'border-zinc-200 hover:border-zinc-300'}`}>
                    <div className="rounded-xl overflow-hidden" style={{ background: '#F8F6F2' }}>
                      <div className="p-6 text-center">
                        <div className="w-16 h-16 mx-auto rounded-full mb-3" style={{ background: '#1A1A1A' }} />
                        <p style={{ color: '#1A1A1A', fontFamily: "'Cinzel', serif" }} className="text-lg font-bold">Jasny</p>
                        <p className="text-zinc-500 text-xs mt-1">Minimalistyczny, czysty</p>
                      </div>
                    </div>
                  </button>
                </div>

                <div className="flex gap-3 mt-8">
                  <Button variant="outline" onClick={() => setStep(1)} className="border-zinc-200 text-zinc-500 rounded-xl">Wstecz</Button>
                  <Button onClick={generate} disabled={loading} className="bg-[#F47A3B] hover:bg-[#E06A2B] rounded-xl px-8" data-testid="generate-btn">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
                    Stwórz reklamę
                  </Button>
                </div>
              </div>
            )}

            {/* ─── STEP 3: Preview ─── */}
            {step === 3 && posterData && (
              <div data-testid="step-preview">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-[18px] font-bold text-zinc-900">Twoja reklama jest gotowa!</h2>
                    <p className="text-[14px] text-zinc-500 mt-1">Pobierz lub opublikuj od razu.</p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setStep(2)} className="border-zinc-200 text-zinc-500 rounded-xl">Zmień styl</Button>
                    <Button onClick={exportPNG} disabled={exporting} className="bg-[#F47A3B] hover:bg-[#E06A2B] rounded-xl px-6" data-testid="download-btn">
                      {exporting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
                      Pobierz reklamę
                    </Button>
                  </div>
                </div>

                <div className="flex justify-center">
                  <div style={{ transform: 'scale(0.45)', transformOrigin: 'top center', width: 1080, height: 1350 }}>
                    <div ref={posterRef}><PosterTemplate data={posterData} theme={theme} /></div>
                  </div>
                </div>

                {/* CO TERAZ — po wygenerowaniu */}
                <div className="bg-[#F47A3B]/10 rounded-2xl border border-[#F47A3B]/20 p-6 mt-6" data-testid="post-generate-actions">
                  <h3 className="text-[16px] font-bold text-zinc-900 mb-4 flex items-center gap-2">
                    <Rocket className="w-5 h-5 text-[#F47A3B]" />
                    Co teraz?
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <button onClick={exportPNG} className="flex items-center gap-3 p-4 bg-white rounded-xl border border-zinc-200 hover:border-[#F47A3B]/40 transition text-left">
                      <Download className="w-5 h-5 text-[#F47A3B]" />
                      <div>
                        <p className="text-[14px] font-semibold text-zinc-900">Pobierz reklamę</p>
                        <p className="text-[12px] text-zinc-500">Zapisz na dysku</p>
                      </div>
                    </button>
                    <Link to="/panel/booster" className="flex items-center gap-3 p-4 bg-white rounded-xl border border-zinc-200 hover:border-[#F47A3B]/40 transition text-left">
                      <Facebook className="w-5 h-5 text-[#1877F2]" />
                      <div>
                        <p className="text-[14px] font-semibold text-zinc-900">Opublikuj na FB</p>
                        <p className="text-[12px] text-zinc-500">Udostepnij od razu</p>
                      </div>
                    </Link>
                    <Link to="/panel/booster" className="flex items-center gap-3 p-4 bg-white rounded-xl border border-zinc-200 hover:border-[#F47A3B]/40 transition text-left">
                      <Calendar className="w-5 h-5 text-[#34A853]" />
                      <div>
                        <p className="text-[14px] font-semibold text-zinc-900">Zaplanuj publikacje</p>
                        <p className="text-[12px] text-zinc-500">Wybierz date i godzine</p>
                      </div>
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* RIGHT COLUMN — Assistant */}
          <div className="lg:col-span-4" data-testid="campaign-right-column">
            <div className="sticky top-[80px]">
              <AssistantPanel
                whatNow={step === 0
                  ? 'Stwórz pierwszą reklamę — to zajmie mniej niż minutę.'
                  : step < 3
                  ? 'Wybierz co chcesz promowac i styl reklamy. System stworzy gotowy material.'
                  : 'Pobierz reklamę lub opublikuj ja od razu na swoich kanalach.'}
              />
            </div>
          </div>
        </div>
      </div>
    </PanelLayout>
  );
}
